import type { Member } from './api.interface';

export const QUOTA_CYCLE_DAYS = 30;

export interface CycleQuotaState {
  cycleStartDate: string;
  cycleUsed: number;
  cycleBonus: number;
  cycleDeduction: number;
  nextCycleBorrowed: number;
}

export function getNextCycleStart(start: string): string {
  const date = new Date(`${start}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) throw new Error('周期日期无效');
  date.setUTCDate(date.getUTCDate() + QUOTA_CYCLE_DAYS);
  return date.toISOString().slice(0, 10);
}

/**
 * 本周期已经用掉的预支次数：先用完本周期的正常额度，超出部分才算用了预支。
 * 下个周期只扣这部分，预支了没用完的会自动退回。
 */
export function getUsedBorrowedQuota(monthlyQuota: number, state: Pick<CycleQuotaState, 'cycleUsed' | 'cycleDeduction' | 'nextCycleBorrowed'>): number {
  const ownQuota = Math.max(0, monthlyQuota - state.cycleDeduction);
  return Math.min(state.nextCycleBorrowed, Math.max(0, state.cycleUsed - ownQuota));
}

export function settleQuotaCycle(state: CycleQuotaState, today: string, monthlyQuota: number): CycleQuotaState {
  if (!state.cycleStartDate) return { ...state, cycleStartDate: today };
  const elapsed = Math.floor((Date.parse(today) - Date.parse(state.cycleStartDate)) / 86400000);
  if (elapsed < QUOTA_CYCLE_DAYS) return state;
  // Preserve the existing thirty-day, reset-on-access cycle policy. A debt is
  // charged only in its immediately following cycle, never at a much later login.
  return {
    cycleStartDate: today, cycleUsed: 0, cycleBonus: 0,
    cycleDeduction: elapsed < QUOTA_CYCLE_DAYS * 2 ? getUsedBorrowedQuota(monthlyQuota, state) : 0,
    nextCycleBorrowed: 0,
  };
}

export function getCycleRemaining(monthlyQuota: number, state: Pick<CycleQuotaState, 'cycleUsed' | 'cycleBonus' | 'cycleDeduction'>): number {
  return Math.max(0, monthlyQuota + state.cycleBonus - state.cycleDeduction - state.cycleUsed);
}

export function getMemberCycleRemaining(member: Member): number {
  return member.quota_mode === 'total'
    ? Math.max(0, (member.total_quota ?? 0) - member.total_redemptions)
    : getCycleRemaining(member.monthly_quota, {
      cycleUsed: member.cycle_used,
      cycleBonus: member.cycle_bonus ?? 0,
      cycleDeduction: member.cycle_deduction ?? 0,
    });
}

export function getMemberUsedBorrowedQuota(member: Member): number {
  if (member.quota_mode === 'total') return 0;
  return getUsedBorrowedQuota(member.monthly_quota, {
    cycleUsed: member.cycle_used,
    cycleDeduction: member.cycle_deduction ?? 0,
    nextCycleBorrowed: member.next_cycle_borrowed ?? 0,
  });
}

export function advanceNextCycleQuota(state: CycleQuotaState, amount: number, monthlyQuota: number, expiryDate: string): CycleQuotaState {
  if (!Number.isSafeInteger(amount) || amount < 0 || amount > 2147483647) {
    throw new Error('提前使用额度必须是大于等于0的整数');
  }
  if (amount === 0) return state;
  if (!state.cycleStartDate || getNextCycleStart(state.cycleStartDate) > expiryDate) {
    throw new Error('会员有效期内没有下一个额度周期，不能提前使用');
  }
  if (amount > Math.max(0, monthlyQuota - state.nextCycleBorrowed)) {
    throw new Error('不能超过下一个周期的剩余额度');
  }
  if (state.cycleBonus + amount > 2147483647) throw new Error('额度超出允许范围');
  return { ...state, cycleBonus: state.cycleBonus + amount, nextCycleBorrowed: state.nextCycleBorrowed + amount };
}
